import React, { useContext } from 'react';
import { UIProviderContext } from '../../providers/UIProvider/context';
import { useNavigate } from 'react-router-dom';
import { Components } from './styled';
import SideBarLink from './SideBarLink';
import ContentManagementNav from './ContentManagementNav';
import { ReactComponent as Logo } from '../../assets/logo/sidebar-logo.svg';
import { ReactComponent as LogoIcon } from '../../assets/logo/sidebar-logo-icon.svg';
import { ReactComponent as CalendarIcon } from '../../assets/sidebarIcons/icon-calendar-day.svg';
import { ReactComponent as CalendarIconActive } from '../../assets/sidebarIcons/icon-calendar-day-active.svg';
import { ReactComponent as BookIcon } from '../../assets/sidebarIcons/icon-book-open.svg';
import { ReactComponent as BookIconActive } from '../../assets/sidebarIcons/icon-book-open-active.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/sidebarIcons/icon-envelope-open-text.svg';
import { ReactComponent as EnvelopeIconActive } from '../../assets/sidebarIcons/icon-envelope-open-text-active.svg';
import { ReactComponent as GroupIcon } from '../../assets/sidebarIcons/icon-layer-group.svg';
import { ReactComponent as GroupIconActive } from '../../assets/sidebarIcons/icon-layer-group-active.svg';
import { ReactComponent as DollarIcon } from '../../assets/sidebarIcons/icon-comment-dollar.svg';
import { ReactComponent as DollarIconActive } from '../../assets/sidebarIcons/icon-comment-dollar-active.svg';
import { ReactComponent as TabletIcon } from '../../assets/sidebarIcons/icon-tablet-alt.svg';
import { ReactComponent as TabletIconActive } from '../../assets/sidebarIcons/icon-tablet-alt-active.svg';
import { ReactComponent as UserIcon } from '../../assets/sidebarIcons/icon-user.svg';
import { ReactComponent as UserIconActive } from '../../assets/sidebarIcons/icon-user-active.svg';
import { ReactComponent as ChatIcon } from '../../assets/sidebarIcons/icon-chat.svg';
import { ReactComponent as ChatIconActive } from '../../assets/sidebarIcons/icon-chat-active.svg';
import { ReactComponent as ChartIcon } from '../../assets/sidebarIcons/icon-chart-bar.svg';
import { ReactComponent as ChartIconActive } from '../../assets/sidebarIcons/icon-chart-bar-active.svg';
import { ReactComponent as UsersIcon } from '../../assets/sidebarIcons/icon-users-route.svg';
import { ReactComponent as UsersIconActive } from '../../assets/sidebarIcons/icon-users-route-active.svg';
import changelog from '../../changelog.json';
import useModalState from '../../hooks/useModalState';
import ChangelogModal from '../modals/ChangelogModal';
import { UIView } from '../../utils/styled/styled';
import { SessionContext } from '../../providers/SessionProvider/context';
import { revertUserImpersonation } from '../../api/user';
import { ProjectErrors } from '../../utils/errors/types';
import { confirmationTexts } from '../modals/ConfirmationModal/utils';
import ConfirmationModal from '../modals/ConfirmationModal';
import { ActivityIndicatorContext } from '../../providers/ActivityIndicatorProvider/context';
import { ToastContext } from '../../providers/ToastProvider/context';

const ProjectSideBar = () => {
  const navigate = useNavigate();

  const { currentProfile, refreshProfile } = useContext(SessionContext);
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { showErrorToast, showSuccessToast, showWarningToast } = useContext(ToastContext);

  const changelogModal = useModalState();
  const confirmationModalRevertUserImpersonation = useModalState<boolean>();

  const { isSidebarCollapsed: isCollapsed, toggleSidebarCollapsed: onToggleCollapsed } = useContext(UIProviderContext);

  const onLogoPress = () => {
    navigate('/lessons');
  };

  const onLogoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onLogoPress();
    }
  };

  const _onVersionClick = () => {
    changelogModal.open(true);
  };

  const onConfirmRevertUserImpersonation = () => {
    confirmationModalRevertUserImpersonation.open(true);
  };

  const onRevertUserImpersonation = async () => {
    setIsLoading(true);
    const result = await revertUserImpersonation();
    setIsLoading(false);
    const isSuccessful = !!result?.success;
    if (isSuccessful) {
      showSuccessToast('The impersonation was successfully removed');
      refreshProfile().then();
    } else {
      // @ts-ignore
      showErrorToast(result?.getUserMessage?.() || result?.message || ProjectErrors.genericTryAgain);
    }
  };

  const toggleLabel = isCollapsed ? 'Expand sidebar' : 'Collapse sidebar';

  return (
    <Components.Container $isCollapsed={isCollapsed}>
      <Components.HeaderRow $isCollapsed={isCollapsed}>
        {isCollapsed ? (
          <Components.CollapsedLogoWrapper
            role={'link'}
            tabIndex={0}
            aria-label={'Alux home'}
            title={'Alux home'}
            onClick={onLogoPress}
            onKeyDown={onLogoKeyDown}>
            <LogoIcon />
          </Components.CollapsedLogoWrapper>
        ) : (
          <Components.LogoContainer
            role={'link'}
            tabIndex={0}
            aria-label={'Alux home'}
            onClick={onLogoPress}
            onKeyDown={onLogoKeyDown}>
            <Logo />
          </Components.LogoContainer>
        )}
        <Components.ToggleButton
          type={'button'}
          aria-label={toggleLabel}
          aria-expanded={!isCollapsed}
          title={toggleLabel}
          onClick={onToggleCollapsed}>
          <Components.ToggleIcon $isCollapsed={isCollapsed} aria-hidden={'true'} />
        </Components.ToggleButton>
      </Components.HeaderRow>
      <Components.InnerContainer>
        <SideBarLink
          label={'Lessons'}
          to={'/lessons'}
          IconActive={<BookIconActive />}
          Icon={<BookIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'Daily Alux Calendar'}
          to={'/daily-alux'}
          IconActive={<CalendarIconActive />}
          Icon={<CalendarIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'Content Groups'}
          to={'/content-groups'}
          IconActive={<GroupIconActive />}
          Icon={<GroupIcon />}
          isCollapsed={isCollapsed}
        />
        <SideBarLink
          label={'App Configuration'}
          to={'/app-configuration'}
          IconActive={<TabletIconActive />}
          Icon={<TabletIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'Banner Offers'}
          to={'/banner-offers'}
          IconActive={<DollarIconActive />}
          Icon={<DollarIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <ContentManagementNav isCollapsed={isCollapsed} />
        <SideBarLink
          label={'Campaigns'}
          to={'/campaigns'}
          IconActive={<DollarIconActive />}
          Icon={<DollarIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'Lesson Statistics'}
          to={'/lesson-stats'}
          IconActive={<ChartIconActive />}
          Icon={<ChartIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'User Lookup'}
          to={'/user-lookup'}
          IconActive={<UserIconActive />}
          Icon={<UserIcon />}
          isCollapsed={isCollapsed}
        />
        <SideBarLink
          label={'Forms'}
          to={'/forms'}
          IconActive={<EnvelopeIconActive />}
          Icon={<EnvelopeIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'Prompt management'}
          to={'/prompt'}
          IconActive={<ChatIconActive />}
          Icon={<ChatIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'Default Networks'}
          to={'/networks'}
          IconActive={<UsersIconActive />}
          Icon={<UsersIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'User Paths'}
          to={'/user-paths'}
          IconActive={<GroupIconActive />}
          Icon={<GroupIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
        <SideBarLink
          label={'Transcripts'}
          to={'/transcripts'}
          IconActive={<ChatIconActive />}
          Icon={<ChatIcon />}
          isCollapsed={isCollapsed}
          activeOnlyWhenExact
        />
      </Components.InnerContainer>

      {currentProfile?.isImpersonating ? (
        // <Components.FieldWrapper>
        <Components.ImpersonateButton
          $isCollapsed={isCollapsed}
          title={'Revert user impersonation'}
          onClick={onConfirmRevertUserImpersonation}>
          {isCollapsed ? 'Revert' : 'Revert user impersonation'}
        </Components.ImpersonateButton>
      ) : // </Components.FieldWrapper>
      null}

      <ConfirmationModal
        {...confirmationModalRevertUserImpersonation}
        confirmationText={confirmationTexts.users.revertUserImpersonation}
        onConfirm={onRevertUserImpersonation}
      />

      <UIView justifyContent={'flex-end'} flex={1}>
        {!isCollapsed && (
          <Components.CollapseButtonText onClick={_onVersionClick}>
            v{changelog?.versions?.[0]?.version || '1.0.0'}
          </Components.CollapseButtonText>
        )}
      </UIView>

      <ChangelogModal {...changelogModal} changelog={changelog} />
    </Components.Container>
  );
};

export default ProjectSideBar;
